import React, { useState, useRef, useEffect } from 'react';
import './CountryAutocomplete.css';

const CountryAutocomplete = ({ options, value, onChange, placeholder = 'Pesquisar país ou jogador...', stickers }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normInput = norm(inputValue);

    let filtered = inputValue.trim() === ''
      ? []
      : options.filter((opt) => {
          if (opt.type === 'group') {
            return norm(opt.label).includes(normInput) || norm(opt.value).includes(normInput);
          }
          // jogadores: busca no nome
          return norm(opt.label).includes(normInput);
        });

    filtered = filtered.slice(0, 20);

    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [inputValue, options]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Abrir dropdown apenas se houver texto digitado
    setIsOpen(newValue.trim().length > 0);
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setInputValue('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="country-autocomplete" ref={containerRef}>
      <div className="autocomplete-input-wrapper">
        <span className="autocomplete-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          className="autocomplete-input"
          placeholder={selectedOption ? `${selectedOption.label} - Clique para mudar` : placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          aria-label="Pesquisar país"
          aria-autocomplete="list"
          aria-expanded={isOpen}
        />
        {value && (
          <button
            className="autocomplete-clear"
            onClick={handleClear}
            aria-label="Limpar seleção"
            type="button"
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <ul className="autocomplete-dropdown" role="listbox">
          {filteredOptions.map((option, index) => (
            <li
              key={`${option.value}-${option.type}-${option.playerKey || index}`}
              className={`autocomplete-option ${option.type === 'player' ? 'player-option' : ''} ${
                index === highlightedIndex ? 'highlighted' : ''
              } ${value === option.value && option.type === 'group' ? 'selected' : ''}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={value === option.value && option.type === 'group'}
            >
              {option.type === 'player' ? (
                <span className="player-result">
                  <span className="player-country">{option.countryFlag} {option.countryName}</span>
                  <span className="player-name-row">
                    <span className="player-name">{option.label}</span>
                    {stickers && (
                      stickers[option.value]?.jogadores[option.playerKey]?.obtida
                        ? <span className="player-status player-status--have">Tenho</span>
                        : <span className="player-status player-status--missing">Falta</span>
                    )}
                  </span>
                </span>
              ) : (
                option.label
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen && inputValue.trim() !== '' && filteredOptions.length === 0 && (
        <div className="autocomplete-no-results">Nenhum resultado encontrado</div>
      )}
    </div>
  );
};

export default CountryAutocomplete;
